import {
  type GetSpaceRequest,
  type ListSpacesResponseValid,
  type GetSpaceResponseValid,
  type SpaceValid,
  SpaceService,
} from '@qdrant/qdrant-cloud-public-api/serverless/space/v1';
import {
  listSpaces,
  getSpace,
  createSpace,
  createSpaceFromBackup,
  updateSpace,
  deleteSpace,
  suggestSpaceName,
} from '@qdrant/qdrant-cloud-public-api/serverless/space/v1/connect-query';
import { transport } from '@/api/transport';
import {
  createConnectQueryKey,
  createServiceQueries,
  type MessageInput,
  type MutationFactory,
  queryOptions,
  useMutation,
} from '@/connect-query';

/** How often a serverless query polls while what it reads is still in progress. */
export const SPACE_BUSY_POLL_MS = 5_000;

/** The ids every space request is addressed by. */
export type SpaceIds = Pick<MessageInput<GetSpaceRequest>, 'accountId' | 'spaceId'>;

export const getSpaceEndpointUrl = (space: SpaceValid) => space.state?.endpoint?.url;

// GRPC types
export const spaceService = createServiceQueries(SpaceService, {
  suggestSpaceNameQuery: (params) => queryOptions(suggestSpaceName, params, { transport, select: (d) => d.name }),
  listSpacesQuery: (params) => {
    const queryOpts = queryOptions(listSpaces, params, { transport });

    return {
      ...queryOpts,
      queryFn: async (p) => {
        const result = await queryOpts.queryFn(p);

        return result as ListSpacesResponseValid & { items: SpaceValid[] };
      },
    };
  },
  getSpaceQuery: (params) => {
    const queryOpts = queryOptions(getSpace, params, { transport });

    return {
      ...queryOpts,
      queryFn: async (p) => {
        const result = await queryOpts.queryFn(p);

        return result as GetSpaceResponseValid;
      },
    };
  },
});

export const useCreateSpaceMutation: MutationFactory<typeof createSpace> = () =>
  useMutation(createSpace, {
    onSuccess: (data, _variables, _onMutateResult, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listSpaces,
            input: { accountId: data.space.accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: suggestSpaceName,
            input: { accountId: data.space.accountId },
          }),
        }),
      ]),
  });

export const useCreateSpaceFromBackupMutation: MutationFactory<typeof createSpaceFromBackup> = () =>
  useMutation(createSpaceFromBackup, {
    onSuccess: (data, _variables, _onMutateResult, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listSpaces,
            input: { accountId: data.space.accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: suggestSpaceName,
            input: { accountId: data.space.accountId },
          }),
        }),
      ]),
  });

export const useUpdateSpaceMutation: MutationFactory<typeof updateSpace> = () =>
  useMutation(updateSpace, {
    onSuccess: (data, _variables, _onMutateResult, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listSpaces,
            input: { accountId: data.space.accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getSpace,
            input: { accountId: data.space.accountId, spaceId: data.space.id },
          }),
        }),
      ]),
  });

export const useDeleteSpaceMutation: MutationFactory<typeof deleteSpace> = () =>
  useMutation(deleteSpace, {
    onSuccess: (_data, { accountId }, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listSpaces,
            input: { accountId },
          }),
        }),
        router.invalidate({
          filter: ({ routeId }) => routeId === '/_authenticated/_dashboard/accounts/$accountId',
        }),
      ]),
  });
